import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { tracks as tracksApi } from "../../lib/api";
import { Button } from "../shared/Button";
import { IconButton } from "../shared/IconButton";
import { ChevronLeft, Pencil, Plus, Trash } from "../shared/Icon";
import { StatusDot, statusLabel } from "../shared/StatusDot";
import { formatTokens } from "../../lib/format";
import { useUI } from "../../lib/store";
import type { Task, TaskStatus, Track, TrackStatus } from "../../lib/types";
import { AttachTaskModal } from "./AttachTaskModal";

interface Props {
  track: Track;
  onClose: () => void;
  onEdit?: () => void;
}

export function TrackDetail({ track, onClose, onEdit }: Props) {
  const queryClient = useQueryClient();
  const setSelectedTask = useUI((s) => s.setSelectedTaskId);
  const setRightPaneMode = useUI((s) => s.setRightPaneMode);

  const { data: childTasks = [] } = useQuery({
    queryKey: ["tracks", track.id, "tasks"],
    queryFn: () => tracksApi.getTasks(track.id),
    refetchInterval: 3000,
  });

  // The track-list payload is lean — it omits `plan`, `description`, `models`,
  // and `tokens_used`. Fetch the full track so the detail view has the milestone
  // plan (otherwise milestone sections, and the tasks inside them, never render).
  const { data: fullTrack } = useQuery({
    queryKey: ["tracks", track.id, "detail"],
    queryFn: () => tracksApi.get(track.id),
    refetchInterval: 3000,
  });
  const tr = fullTrack ?? track;

  const dispatchMutation = useMutation({
    mutationFn: () => tracksApi.dispatch(track.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tracks"] });
    },
  });

  const cancelMutation = useMutation({
    mutationFn: () => tracksApi.cancel(track.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tracks"] });
    },
  });

  // `null` = closed; a number = open with that milestone preselected;
  // -1 = open with no milestone preselected (track-level "Add task").
  const [attachMilestone, setAttachMilestone] = useState<number | null>(null);

  const detachMutation = useMutation({
    mutationFn: (taskId: string) => tracksApi.detachTask(track.id, taskId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tracks"] });
      queryClient.invalidateQueries({ queryKey: ["tracks", track.id, "tasks"] });
      queryClient.invalidateQueries({ queryKey: ["kanban"] });
    },
    onError: (e) => {
      // eslint-disable-next-line no-alert
      alert(e instanceof Error ? e.message : String(e));
    },
  });

  const planMilestones = tr.plan?.milestones ?? [];
  const tasksByMilestone = new Map<number, Task[]>();
  for (const t of childTasks) {
    const idx = t.track_milestone ?? 0;
    const list = tasksByMilestone.get(idx) ?? [];
    list.push(t);
    tasksByMilestone.set(idx, list);
  }
  for (const list of tasksByMilestone.values()) {
    list.sort((a, b) => (a.track_ordinal ?? 0) - (b.track_ordinal ?? 0));
  }
  // Show a section for every milestone in the plan AND every milestone that has
  // tasks — so an assigned task is never hidden, even if the plan payload lags
  // or a task lands in a milestone the plan doesn't (yet) list.
  const maxTaskMilestone = childTasks.reduce(
    (m, t) => Math.max(m, t.track_milestone ?? 0),
    -1,
  );
  const milestoneCount = Math.max(planMilestones.length, maxTaskMilestone + 1);
  const milestoneIndices = Array.from({ length: milestoneCount }, (_, i) => i);

  const focusTask = (taskId: string) => {
    setSelectedTask(taskId);
    setRightPaneMode("activity");
  };

  return (
    <>
    <div className="p-5 space-y-4">
      <div className="flex items-start gap-3">
        <IconButton
          icon={<ChevronLeft />}
          label="Back to tracks"
          onClick={onClose}
        />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <StatusDot status={tr.status as TrackStatus} pulse={tr.status === "in_progress"} />
            <div className="text-display text-text-primary truncate">
              {tr.title}
            </div>
          </div>
          <div className="mt-1 text-small text-text-tertiary">
            {statusLabel(tr.status as TrackStatus)} ·{" "}
            {childTasks.length} {childTasks.length === 1 ? "task" : "tasks"} ·{" "}
            {formatTokens(tr.tokens_used ?? 0)} tokens ·{" "}
            milestone {(tr.active_milestone ?? 0) + 1}/{milestoneIndices.length || 1}
            {tr.parallel ? " · parallel" : ""}
          </div>
          {tr.description && (
            <div className="mt-2 text-body text-text-secondary">{tr.description}</div>
          )}
          {Object.keys(tr.models || {}).length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1">
              {Object.entries(tr.models).map(([role, model]) => (
                <span
                  key={role}
                  className="inline-flex items-center text-mono text-text-tertiary border border-border-subtle rounded-md px-2 py-0.5"
                  title={`${role} model`}
                >
                  <span className="text-text-quaternary mr-1">{role}</span>
                  {model}
                </span>
              ))}
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          {onEdit && (
            <IconButton icon={<Pencil />} label="Edit track" onClick={onEdit} />
          )}
          <Button
            variant="secondary"
            onClick={() => setAttachMilestone(-1)}
          >
            Add task
          </Button>
          {(["planning", "ready"] as TrackStatus[]).includes(tr.status) && (
            <Button
              variant="primary"
              disabled={dispatchMutation.isPending}
              onClick={() => dispatchMutation.mutate()}
            >
              Dispatch
            </Button>
          )}
          {(["in_progress", "needs_input", "ready"] as TrackStatus[]).includes(tr.status) && (
            <Button
              variant="danger"
              disabled={cancelMutation.isPending}
              onClick={() => cancelMutation.mutate()}
            >
              Cancel
            </Button>
          )}
        </div>
      </div>

      <div className="space-y-4">
        {milestoneIndices.length === 0 ? (
          <div className="text-small text-text-tertiary">
            No tasks assigned yet. Use <span className="text-text-secondary">Add task</span> to
            assign existing work, or build a plan in the track editor.
          </div>
        ) : (
          milestoneIndices.map((idx) => {
            const milestone = planMilestones[idx];
            const tasks = tasksByMilestone.get(idx) ?? [];
            return (
              <section
                key={idx}
                className="border border-border-subtle rounded-lg overflow-hidden"
              >
                <header className="flex items-center justify-between px-4 py-2 bg-bg-inset border-b border-border-subtle">
                  <div>
                    <div className="text-mono text-text-quaternary">
                      Milestone {idx + 1}
                    </div>
                    <div className="text-heading text-text-primary">
                      {milestone?.title || `Milestone ${idx + 1}`}
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    {milestone?.acceptance && milestone.acceptance.length > 0 && (
                      <div className="text-small text-text-tertiary max-w-md">
                        {milestone.acceptance.length} acceptance criteria
                      </div>
                    )}
                    <IconButton
                      icon={<Plus />}
                      label={`Add task to milestone ${idx + 1}`}
                      size="sm"
                      onClick={() => setAttachMilestone(idx)}
                    />
                  </div>
                </header>
                <ul className="divide-y divide-border-subtle">
                  {tasks.length === 0 ? (
                    <li className="px-4 py-3 text-small text-text-tertiary">
                      Tasks will be materialized when the track is dispatched.
                    </li>
                  ) : (
                    tasks.map((t) => (
                      <li
                        key={t.id}
                        className="group px-4 py-2.5 flex items-center gap-3 hover:bg-bg-inset cursor-pointer"
                        onClick={() => focusTask(t.id)}
                      >
                        <StatusDot
                          status={t.status as TaskStatus}
                          pulse={t.status === "in_progress"}
                        />
                        <div className="flex-1 min-w-0">
                          <div className="text-body text-text-primary truncate">
                            {t.title}
                          </div>
                          <div className="text-mono text-text-quaternary">
                            {t.kind === "validate" ? "validator" : "implement"}
                            {t.validation_verdict ? ` · ${t.validation_verdict}` : ""}
                            {" · "}
                            {formatTokens(t.tokens_used)} tokens
                          </div>
                        </div>
                        <span className="text-small text-text-tertiary">
                          {statusLabel(t.status as TaskStatus)}
                        </span>
                        {t.kind !== "validate" &&
                          (t.status === "todo" || t.status === "backlog") && (
                            <span
                              className="opacity-0 group-hover:opacity-100 transition-opacity duration-fast ease-out"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <IconButton
                                icon={<Trash />}
                                label="Remove from track"
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  detachMutation.mutate(t.id);
                                }}
                              />
                            </span>
                          )}
                      </li>
                    ))
                  )}
                </ul>
              </section>
            );
          })
        )}
      </div>
    </div>
    {attachMilestone !== null && (
      <AttachTaskModal
        track={track}
        defaultMilestoneIdx={attachMilestone >= 0 ? attachMilestone : undefined}
        onClose={() => setAttachMilestone(null)}
      />
    )}
    </>
  );
}
